import { getApiBase } from '@services/apiBase';
import { getAccessToken } from '@services/token';
import { getTurnstileToken } from '@services/turnstile';
import { clientUserAgentHeaders } from '@services/clientInfo';

const API_BASE = getApiBase();

// Internal helper for standardized API requests
export async function apiRequest<T = any>(
  path: string,
  {
    method = 'GET',
    body,
    headers = {},
    auth = true,
    requireAuth = false,
    turnstile = true,
    apiBase = API_BASE,
    resp = 'json' as 'json' | 'text' | 'blob' | 'void' | 'response',
    ignore404 = false,
    errorMap = {},
    ...customConfig
  }: {
    method?: string;
    body?: any;
    headers?: Record<string, string>;
    auth?: boolean;
    requireAuth?: boolean;
    turnstile?: boolean;
    apiBase?: string;
    resp?: 'json' | 'text' | 'blob' | 'void' | 'response';
    ignore404?: boolean;
    errorMap?: Record<number | string, string>;
    [key: string]: any; // Allow generic RequestInit properties (signal, mode, etc.)
  } = {}
): Promise<T> {
  const reqHeaders: Record<string, string> = { ...clientUserAgentHeaders(), ...headers };

  if (auth) {
    const token = getAccessToken();
    if (token) {
      reqHeaders['Authorization'] = `Bearer ${token}`;
    } else if (requireAuth) {
      throw new Error('missing_token');
    }
  }

  if (turnstile) {
    const ts = getTurnstileToken();
    if (ts) reqHeaders['cf-turnstile-response'] = ts;
  }

  const isJson = body && typeof body === 'object' && 
                 !(body instanceof Blob) && 
                 !(body instanceof FormData) && 
                 !(body instanceof Uint8Array) && 
                 !(body instanceof URLSearchParams);

  if (isJson && !reqHeaders['Content-Type']) {
    reqHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${apiBase}${path}`, {
    method,
    headers: reqHeaders,
    body: isJson ? JSON.stringify(body) : body,
    ...customConfig
  });

  if (!response.ok) {
    if (errorMap[response.status]) {
      throw new Error(errorMap[response.status]);
    }
    
    if (ignore404 && response.status === 404) {
      return null as T;
    }

    if (errorMap['default']) {
      throw new Error(errorMap['default']);
    }

    const text = await response.text().catch(() => '');
    let msg = text || response.statusText;
    try {
      const json = JSON.parse(text);
      // Attempt to extract meaningful error message from common formats
      msg = json.error || json.message || json.errorMessage || json.details || msg;
    } catch { /* ignore */ }

    throw new Error(msg);
  }

  if (resp === 'response') return response as unknown as T;
  if (resp === 'void') return undefined as unknown as T;
  if (resp === 'text') return response.text() as unknown as T;
  if (resp === 'blob') return response.blob() as unknown as T;

  if (response.status === 204) return undefined as unknown as T;
  return response.json().catch(() => ({})) as Promise<T>;
}
